import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellOff, Copy, Download, Info, Search, GripVertical, Table2, LayoutGrid } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LEAD_STATUS_LABEL, LEAD_STATUSES } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Native HTML5 drag/drop Kanban. Six lanes = the six lead statuses.
// Drops PATCH /api/leads/{id}/status and optimistically update local state.

function detectIOSInstall() {
  if (typeof navigator === "undefined") return { isIOS: false, isSafari: false, isStandalone: false, canInstall: false };
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const inAppMarkers = /(FBAN|FBAV|Instagram|WhatsApp|Line|MicroMessenger|Twitter|GSA)/i;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) && !inAppMarkers.test(ua);
  const isStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return { isIOS, isSafari, isStandalone, canInstall: isIOS && isSafari && !isStandalone };
}

const LANE_ACCENTS = {
  New: "bg-blue-500",
  Contacted: "bg-indigo-500",
  Qualified: "bg-violet-500",
  "Consultation Booked": "bg-emerald-500",
  Closed: "bg-propertic-gold",
  Lost: "bg-slate-400",
};

export default function AdminLeadsKanban() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const justDraggedRef = useRef(false);
  const [leads, setLeads] = useState([]);
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [notifPermission, setNotifPermission] = useState("unsupported");
  const [deviceCount, setDeviceCount] = useState(null);
  const [hideInstallHint, setHideInstallHint] = useState(false);
  const [view, setView] = useState("kanban"); // 'kanban' | 'table'
  const [draggingId, setDraggingId] = useState(null);
  const [hoverLane, setHoverLane] = useState(null);
  const lastSeenRef = useRef(new Date().toISOString());
  const installInfo = useMemo(() => detectIOSInstall(), []);

  useEffect(() => {
    try { if (typeof Notification !== "undefined") setNotifPermission(Notification.permission); } catch (_e) {}
    try {
      if (typeof localStorage !== "undefined" && localStorage.getItem("hideInstallHint") === "1") {
        setHideInstallHint(true);
      }
      const savedView = typeof localStorage !== "undefined" ? localStorage.getItem("leadsView") : null;
      if (savedView === "table" || savedView === "kanban") setView(savedView);
    } catch (_e) {}
  }, []);

  // Auto re-subscribe on every mount when notifications were previously granted.
  // Fixes iOS Safari's silent PWA subscription expiry (Apple resets push
  // registrations after ~2 weeks of PWA inactivity). Without this the browser
  // thinks it's still subscribed but the server has no endpoint to push to, so
  // the phone goes dark. This effect:
  //   1. Fetches server's current subscription count for this advisor.
  //   2. If 0 and permission is granted, silently re-subscribes and re-posts.
  //   3. Updates the "N devices linked" badge in the header.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      try {
        const my = await api.get("/push/my-subscriptions");
        if (cancelled) return;
        const serverCount = my?.data?.count ?? 0;
        setDeviceCount(serverCount);
        if (serverCount > 0) return; // still linked, nothing to do

        // Server has 0 endpoints for this advisor but the browser is permitted →
        // iOS most likely reset the subscription. Silently re-register.
        const reg = await navigator.serviceWorker.ready;
        const keyRes = await api.get("/push/vapid-public-key");
        const vapidKey = keyRes.data?.key;
        if (!keyRes.data?.configured || !vapidKey) return;
        // Force a clean re-subscription: drop the ghost first if it exists.
        const existing = await reg.pushManager.getSubscription();
        if (existing) { try { await existing.unsubscribe(); } catch {} }
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        const json = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
        await api.post("/push/subscribe", json);
        if (!cancelled) setDeviceCount(1);
      } catch (err) {
        // Silent, badge just stays at whatever the server said.
        console.warn("silent re-subscribe skipped:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
  }

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.set("source", source);
      if (q) params.set("q", q);
      const r = await api.get(`/leads?${params.toString()}`);
      setLeads(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [source]);

  // Poll for new CRM events every 60s.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/crm/events?since=${encodeURIComponent(lastSeenRef.current)}`);
        const events = r.data || [];
        if (events.length && !cancelled) {
          lastSeenRef.current = events[0].created_at;
          for (const ev of events) {
            if (notifPermission === "granted" && typeof Notification !== "undefined") {
              try { new Notification(ev.title || "New lead", { body: ev.body || "", icon: "/propertic-logo.png", tag: ev.id }); } catch {}
            }
            toast.success(ev.title || "New lead");
          }
          load();
        }
      } catch {}
    };
    const iv = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifPermission]);

  const requestNotifications = async () => {
    if (typeof Notification === "undefined") { toast.error("This browser does not support notifications."); return; }
    let perm = Notification.permission;
    if (perm !== "granted") {
      perm = await Notification.requestPermission();
      setNotifPermission(perm);
      if (perm !== "granted") { toast.error("Notifications blocked. Update your browser settings to enable."); return; }
    } else { setNotifPermission("granted"); }
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.warning("Push not supported in this browser. In-tab alerts still work.");
        try { new Notification("Propertic CRM ready", { body: "In-tab alerts are on.", icon: "/propertic-logo.png" }); } catch {}
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await api.get("/push/vapid-public-key");
      const vapidKey = keyRes.data?.key;
      if (!keyRes.data?.configured || !vapidKey) { toast.warning("Server VAPID keys not configured. In-tab alerts still work."); return; }
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      }));
      const json = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
      await api.post("/push/subscribe", json);
      toast.success("Push alerts on. New leads will notify you even with the tab closed.");
    } catch (err) {
      console.error("push subscribe failed", err);
      toast.error("Could not enable background push. In-tab alerts still work.");
    }
  };

  const sendTestPush = async () => {
    try {
      const r = await api.post("/push/test", {});
      toast.success(`Test push sent (${r.data?.delivered ?? 0} device(s)).`);
      // Refresh device count in case some were pruned as gone.
      try {
        const my = await api.get("/push/my-subscriptions");
        setDeviceCount(my?.data?.count ?? 0);
      } catch {}
    } catch (err) {
      const msg = err?.response?.data?.detail || "Test push failed.";
      toast.error(msg);
      if (msg?.includes("No subscriptions")) {
        // Server has none, try one fresh re-subscribe cycle.
        try {
          if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
          const reg = await navigator.serviceWorker.ready;
          const keyRes = await api.get("/push/vapid-public-key");
          const vapidKey = keyRes.data?.key;
          if (!keyRes.data?.configured || !vapidKey) return;
          const existing = await reg.pushManager.getSubscription();
          if (existing) { try { await existing.unsubscribe(); } catch {} }
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
          const json = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
          await api.post("/push/subscribe", json);
          setDeviceCount(1);
          toast.success("Re-linked this device. Tap Test again.");
        } catch {}
      }
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("propertic_token");
      const res = await fetch(`${API_BASE}/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "propertic-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported.");
    } catch { toast.error("Export failed."); }
  };

  // Group leads into lanes.
  const laneMap = useMemo(() => {
    const map = Object.fromEntries(LEAD_STATUSES.map((s) => [s, []]));
    for (const l of leads) (map[l.status] || (map.New)).push(l);
    return map;
  }, [leads]);

  const setLeadStatus = async (leadId, next) => {
    const prev = leads.find((l) => l.id === leadId)?.status;
    if (!prev || prev === next) return;
    // Optimistic update
    setLeads((arr) => arr.map((l) => l.id === leadId ? { ...l, status: next } : l));
    try {
      await api.patch(`/leads/${leadId}/status`, { status: next });
      toast.success(`Moved to "${LEAD_STATUS_LABEL[lang][next]}"`);
    } catch {
      setLeads((arr) => arr.map((l) => l.id === leadId ? { ...l, status: prev } : l));
      toast.error("Failed to update status.");
    }
  };

  const onCardDragStart = (e, lead) => {
    setDraggingId(lead.id);
    justDraggedRef.current = true;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", lead.id); } catch {}
  };
  const onCardDragEnd = () => {
    setDraggingId(null);
    setHoverLane(null);
    // Reset drag flag shortly after so subsequent taps register as clicks
    setTimeout(() => { justDraggedRef.current = false; }, 50);
  };
  const onCardClick = (e, leadId) => {
    if (justDraggedRef.current) return; // ignore click that fires right after a drag
    // ignore clicks originating on interactive children (defensive)
    if (e.target.closest("a,button,input,select,textarea")) return;
    navigate(`/admin/leads/${leadId}`);
  };
  const onLaneDragOver = (e, status) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setHoverLane(status); };
  const onLaneDragLeave = (e, status) => { if (hoverLane === status) setHoverLane(null); };
  const onLaneDrop = (e, status) => {
    e.preventDefault();
    const id = draggingId || (() => { try { return e.dataTransfer.getData("text/plain"); } catch { return null; } })();
    setDraggingId(null); setHoverLane(null);
    if (id) setLeadStatus(id, status);
  };

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("leadsView", v); } catch {}
  };

  return (
    <div data-testid="admin-leads-page">
      {installInfo.canInstall && !hideInstallHint && (
        <div data-testid="ios-install-hint" className="mb-6 rounded-2xl border hairline bg-propertic-sand/60 backdrop-blur px-5 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Info className="w-4 h-4 text-propertic-gold mt-0.5 shrink-0" />
            <div className="text-sm text-propertic-ink leading-relaxed">
              <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-0.5">Install this CRM on your iPhone</p>
              Tap the <span className="font-semibold">Share</span> icon in Safari, then <span className="font-semibold">Add to Home Screen</span>. The CRM opens like a native app and receives lead alerts even when closed.
            </div>
          </div>
          <button
            data-testid="ios-install-dismiss"
            onClick={() => { try { localStorage.setItem("hideInstallHint","1"); } catch {} setHideInstallHint(true); }}
            className="text-[10px] font-mono-brand tracking-[0.18em] uppercase text-propertic-muted hover:text-propertic-navy"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="font-mono-brand text-propertic-gold mb-2">{t("admin.leads")}</p>
          <h1 className="font-editorial text-4xl text-propertic-navy">Lead pipeline</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border hairline bg-white overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setViewPersist("kanban")}
              data-testid="view-toggle-kanban"
              aria-pressed={view === "kanban"}
              className={`h-10 px-3.5 text-[11px] font-mono-brand tracking-[0.14em] uppercase inline-flex items-center gap-1.5 transition-colors ${view === "kanban" ? "bg-propertic-navy text-propertic-paper" : "text-propertic-navy hover:bg-propertic-cream"}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
            <button
              onClick={() => setViewPersist("table")}
              data-testid="view-toggle-table"
              aria-pressed={view === "table"}
              className={`h-10 px-3.5 text-[11px] font-mono-brand tracking-[0.14em] uppercase inline-flex items-center gap-1.5 transition-colors ${view === "table" ? "bg-propertic-navy text-propertic-paper" : "text-propertic-navy hover:bg-propertic-cream"}`}
            >
              <Table2 className="w-3.5 h-3.5" /> Table
            </button>
          </div>
          <button
            onClick={requestNotifications}
            data-testid="enable-notifications-btn"
            title={notifPermission === "granted" ? "Notifications enabled" : "Enable browser + background push"}
            className={`h-10 px-4 rounded-full border hairline text-xs font-mono-brand inline-flex items-center gap-2 transition-colors ${notifPermission === "granted" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white text-propertic-navy hover:border-propertic-gold"}`}
          >
            {notifPermission === "granted" ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
            {notifPermission === "granted" ? "Alerts on" : "Enable alerts"}
          </button>
          {notifPermission === "granted" && (
            <button
              onClick={sendTestPush}
              data-testid="test-push-btn"
              className="h-10 px-3 rounded-full border hairline text-xs font-mono-brand inline-flex items-center gap-2 bg-white text-propertic-muted hover:border-propertic-gold hover:text-propertic-navy transition-colors"
            >
              Test push
            </button>
          )}
          <button onClick={exportCsv} data-testid="export-csv-btn" className="btn-gold rounded-full h-10 px-5 text-xs font-mono-brand inline-flex items-center gap-2">
            <Download className="w-3.5 h-3.5" /> {t("admin.export")}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-[1fr_180px_120px] gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-propertic-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={t("admin.search")}
            data-testid="leads-search-input"
            className="w-full bg-white rounded-xl border hairline h-11 ps-9 pe-3 text-sm text-propertic-navy focus:border-propertic-gold outline-none transition-colors"
          />
        </div>
        <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="leads-source-filter" className="bg-white border hairline text-propertic-navy h-11 rounded-xl">
            <SelectValue placeholder={t("admin.source")} />
          </SelectTrigger>
          <SelectContent className="bg-white border hairline">
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="consultation">Consultation</SelectItem>
            <SelectItem value="loan">Loan</SelectItem>
            <SelectItem value="chatbot">Chatbot</SelectItem>
            <SelectItem value="contact">Contact</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={load} data-testid="leads-refresh-btn" className="h-11 rounded-xl border hairline text-xs font-mono-brand text-propertic-navy hover:bg-propertic-navy hover:text-white transition-colors">
          Filter
        </button>
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border hairline p-10 text-center text-propertic-muted font-mono-brand" data-testid="leads-loading">Loading…</div>
      )}

      {!loading && view === "kanban" && (
        <div className="overflow-x-auto pb-6 -mx-2 px-2" data-testid="leads-kanban">
          <div className="flex gap-4 min-w-max">
            {LEAD_STATUSES.map((status) => {
              const items = laneMap[status] || [];
              const isHover = hoverLane === status;
              return (
                <div
                  key={status}
                  data-testid={`lane-${status.replace(/\s+/g, "-").toLowerCase()}`}
                  onDragOver={(e) => onLaneDragOver(e, status)}
                  onDragLeave={(e) => onLaneDragLeave(e, status)}
                  onDrop={(e) => onLaneDrop(e, status)}
                  className={`w-[300px] shrink-0 rounded-2xl border ${isHover ? "border-propertic-gold bg-propertic-sand/40" : "border-propertic-hairline bg-white"} transition-colors flex flex-col max-h-[calc(100vh-260px)]`}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b hairline">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-2 h-2 rounded-full ${LANE_ACCENTS[status] || "bg-propertic-navy"}`} />
                      <span className="font-mono-brand text-[11px] tracking-[0.14em] uppercase text-propertic-navy truncate">
                        {LEAD_STATUS_LABEL[lang][status]}
                      </span>
                    </div>
                    <span className="text-[11px] font-mono-brand text-propertic-muted tabular">{items.length}</span>
                  </div>
                  <div className="p-2 space-y-2 overflow-y-auto grow">
                    {items.length === 0 && (
                      <p className="text-center text-[11px] text-propertic-muted/80 font-mono-brand py-6">Drop leads here</p>
                    )}
                    {items.map((l) => (
                      <div
                        key={l.id}
                        draggable
                        role="button"
                        tabIndex={0}
                        onDragStart={(e) => onCardDragStart(e, l)}
                        onDragEnd={onCardDragEnd}
                        onClick={(e) => onCardClick(e, l.id)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/admin/leads/${l.id}`); } }}
                        data-testid={`lead-card-${l.id}`}
                        className={`bg-white border ${draggingId === l.id ? "border-propertic-gold opacity-60" : "border-propertic-hairline hover:border-propertic-gold"} rounded-xl p-3 cursor-pointer active:cursor-grabbing transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-propertic-gold/50`}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-3.5 h-3.5 text-propertic-muted/60 mt-0.5 shrink-0 cursor-grab" />
                          <div className="min-w-0 flex-1">
                            <p className="font-editorial text-[15px] text-propertic-navy block truncate">
                              {l.full_name || "—"}
                            </p>
                            {l.email && <p className="text-[11px] text-propertic-muted mt-0.5 truncate">{l.email}</p>}
                            {l.phone && <p className="text-[11px] text-propertic-ink/70 mt-0.5 tabular truncate">{l.phone}</p>}
                            <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t hairline">
                              <span className="font-mono-brand text-[9px] tracking-[0.14em] uppercase text-propertic-gold">{l.source}</span>
                              <span className="text-[10px] text-propertic-muted font-mono-brand">
                                {new Date(l.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && view === "table" && (
        <div className="bg-white rounded-2xl border hairline overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b hairline bg-propertic-cream/60 text-propertic-muted">
                <th className="text-start p-4 font-mono-brand">Name</th>
                <th className="text-start p-4 hidden md:table-cell font-mono-brand">Contact</th>
                <th className="text-start p-4 hidden lg:table-cell font-mono-brand">Source</th>
                <th className="text-start p-4 font-mono-brand">Status</th>
                <th className="text-start p-4 hidden md:table-cell font-mono-brand">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {leads.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-propertic-muted">{t("admin.no_leads")}</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-propertic-cream/40 transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4">
                    <Link to={`/admin/leads/${l.id}`} className="text-propertic-navy hover:text-propertic-gold transition-colors font-medium">
                      {l.full_name}
                    </Link>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <div className="text-propertic-ink/80 text-[13px]">{l.email}</div>
                    <div className="text-propertic-muted text-[11px]">{l.phone}</div>
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className="font-mono-brand text-propertic-ink/70 border hairline px-2.5 py-1 rounded-full">{l.source}</span>
                  </td>
                  <td className="p-4">
                    <span className="font-mono-brand text-propertic-gold border border-propertic-gold/30 bg-propertic-cream px-2.5 py-1 rounded-full">{LEAD_STATUS_LABEL[lang][l.status]}</span>
                  </td>
                  <td className="p-4 hidden md:table-cell text-propertic-muted text-[11px] font-mono-brand">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
